import Component from "../../lib/Component.js";

/**
 * Creates a component that observes elements and triggers a callback when they enter the viewport
 * @param {{
 *  componentName?: string,
 *  observables: Element|Element[]|NodeListOf<Element>,
 *  onEnter?: (element: Element, observer: IntersectionObserver) => void,
 *  unobserveOnEnter?: boolean,
 *  disconnectOnFirst?: boolean,
 *  options?: IntersectionObserverInit
 * }} param0
 * @returns {Component}
 */
const ScrollInView = ({
  componentName,
  observables,
  onEnter,
  unobserveOnEnter = false,
  disconnectOnFirst = false,
  options,
}) => {
  // Normalize observables to a Set (handles single elements, NodeLists, arrays, etc.)
  /** @type {Set<Element>} */
  const elements = observables ? new Set(observables.length != null ? observables : [observables]) : new Set();

  if (elements.size === 0) {
    return Component.noop();
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (elements.has(entry.target) && entry.isIntersecting) {
        if (onEnter) {
          onEnter(entry.target, observer);
        }
        if (unobserveOnEnter) {
          observer.unobserve(entry.target);
        }
        if (disconnectOnFirst) {
          observer.disconnect();
        }
      }
    });
  }, options);

  return new Component({
    componentName: componentName ?? "ScrollInView",
    onMount: () => {
      elements.forEach((element) => observer.observe(element));
    },
    onUnmount: () => {
      observer.disconnect();
    },
  });
};

export default ScrollInView;
